import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { DropzoneArea } from 'react-mui-dropzone';
import { trim } from 'lodash';
import papaparse from 'papaparse';
import CsvTable from './CsvTable';
import { getCountryCode3ByName } from 'utils/countries';
import { ICsvData, IInvalidData } from './index';
import { LoadingButton } from '@mui/lab';
import { UploadFile } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { useTranslation } from 'react-i18next';

const invalidTableColumn = [
  {
    title: 'Row',
    dataIndex: 'rowNumber',
    key: 'rowNumber',
  },
  {
    title: 'Column',
    dataIndex: 'column',
    key: 'column',
  },
  {
    title: 'Error',
    dataIndex: 'error',
    key: 'error',
  },
];

function validAndConvertCsvData(item: ICsvData): Pick<IInvalidData, 'column' | 'error'> | void {
  if (item.productDescription) {
    return;
  }
  item.companyName = trim(item.companyName);
  if (item.companyName.length < 2) {
    return {
      error: '"Company Name" does not seem valid',
      column: 'companyName',
    };
  }

  item.countryCode3 = trim(item.countryCode3) || '';

  // valid country code 3 digit letter and number
  if (item.countryCode3?.length !== 3) {
    return {
      error: `"${item.countryCode3}" is not a valid, expect an official country name or Alpha3 Country Code`,
      column: 'country',
    };
  }
}

type IOnUpload = (list: ICsvData[]) => Promise<void>;

export default function CsvUploadSteps({
  onUpload,
  onFinish,
}: {
  onUpload: IOnUpload;
  onFinish: () => void;
}) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [file, setFile] = React.useState<File>();
  const [tabValue, setTabValue] = React.useState('1');
  const [readyCsvData, setReadyCsvData] = React.useState<ICsvData[]>([]);
  const [invalidCsvData, setInvalidCsvData] = React.useState<IInvalidData[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const { t } = useTranslation('default');
  const steps = [t('select-a-csv-file'), t('validate-and-preview')];

  const exampleColumns = [
    {
      title: t('company-name'),
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: t('country-code'),
      dataIndex: 'countryCode3',
      key: 'countryCode3',
    },
    {
      title: t('Product Description'),
      dataIndex: 'productDescription',
      key: 'productDescription',
    },
    {
      title: t('Transaction ID'),
      dataIndex: 'transactionId',
      key: 'transactionId',
    },
    {
      title: t('HS Code(6)'),
      dataIndex: 'hsCode6',
      key: 'HS Code6',
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleUpload = async () => {
    // do the upload and get result
    setUploading(true);
    try {
      await onUpload(readyCsvData);
    } catch (e) {
      console.error(e);

      setUploadError(e instanceof Error ? e.message : 'Upload failed, please try again later');
    }
    setUploading(false);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateHeaders = (headers: string[]) => {
    const expectedHeaders = [
      'Company Name',
      'Company Country',
      'Product Description',
      'Transaction ID',
      'HS Code(6)',
    ];
    if (headers.length !== expectedHeaders.length) {
      return false;
    }
    for (let i = 0; i < headers.length; i++) {
      if (headers[i] !== expectedHeaders[i]) {
        return false;
      }
    }
    return true;
  };

  const extractCompayListFromCsv = (csvString: string) => {
    const res = papaparse.parse(csvString);
    const headers = res.data[0] as string[];
    const isValidHeader = validateHeaders(headers);
    if (!isValidHeader) {
      return null;
    }
    const firstCell = headers[0];
    const hasHead = firstCell === 'Company Name';
    const guessIndex = (name: string, defaultIndex: number): number => {
      const lowerHeaders = headers.map((i) => i.toLocaleLowerCase());
      const idx = lowerHeaders.indexOf(name.toLocaleLowerCase());
      return idx >= 0 ? idx : defaultIndex;
    };
    const rows = res.data as string[][];
    const filterRow = rows.filter((row: string[]) => row.find((element) => !!element)); // reject blank rows
    const filterColumn: [string, string, string, string, string][] =
      filterRow.map((item) => {
        return [
          item[guessIndex('Company Name', 0)],
          item[guessIndex('Company Country', 1)],
          item[guessIndex('Product Description', 2)],
          item[guessIndex('Transaction ID', 3)],
          item[guessIndex('HS Code(6)', 4)],
        ];
      });
    if (hasHead) {
      // remove the header
      filterColumn.shift();
    }
    return {
      meta: {
        hasHead,
      },
      rows: filterColumn,
    };
  };

  const processContentString = (csvString: string) => {
    const companyNameMap: Map<string, number> = new Map();
    const productDescriptionMap: Map<string, number> = new Map();
    const invalid: IInvalidData[] = [];
    const filteredData = extractCompayListFromCsv(csvString);
    if (!filteredData) {
      invalid.push({
        key: 0,
        rowNumber: 0,
        column: '',
        error: 'Wrong CSV format has been provided. Upload correct format.',
      });
    } else {
      const hasHead = filteredData.meta.hasHead;
      const rowNumberOffset = hasHead ? 2 : 1;
      const ready: ICsvData[] = [];
      filteredData.rows.forEach((item, index: number) => {
        const [
          companyName,
          countryInput,
          productDescription,
          transactionId,
          hsCode6,
        ] = item;
        console.log(item);
        const obj: ICsvData = {
          key: index,
          rowNumber: index + rowNumberOffset,
          companyName,
          countryCode3: getCountryCode3ByName(countryInput),
          productDescription,
          transactionId,
          hsCode6,
        };
        const companyNameKey = (obj.companyName || '') + (obj.countryCode3 || '');
        const productDescriptionKey = (obj.productDescription || '') + (obj.countryCode3 || '');

        const err = validAndConvertCsvData(obj);

        if (!err) {
          ready.push(obj);
          if (obj.companyName) {
            companyNameMap.set(companyNameKey, obj.rowNumber);
          }
          if (obj.productDescription) {
            productDescriptionMap.set(productDescriptionKey, obj.rowNumber);
          }
        } else {
          invalid.push({
            key: index,
            rowNumber: index + rowNumberOffset,
            column: err.column,
            error: err.error,
          });
        }
      });

      setReadyCsvData(ready);
    }
    setInvalidCsvData(invalid);
  };

  const readCsv = (f: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(f, 'utf8');
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
    });
  };


  const handleCsvSelected = async (files: File[]) => {
    const file = files[0];
    if (!file || !(file instanceof File)) {
      setFile(undefined);
      setReadyCsvData([]);
      setInvalidCsvData([
        {
          key: 0,
          rowNumber: 0,
          column: '',
          error: 'No valid CSV file selected.',
        },
      ]);
      return;
    }
    setFile(file);
    const csvString = await readCsv(file);
    processContentString(csvString);
  };

  const renderCSVUpload = () => {
    return (
      <React.Fragment>
        <Typography sx={{ mt: 2, mb: 1 }}>
          <Typography>
            1. {t('load-the-input-company-list-file-in-a-csv-format-described-below')}
          </Typography>
          <Typography>2. {t('the-input-format-company-name-country-iso-code-3-name')}</Typography>
        </Typography>

        <DropzoneArea
          acceptedFiles={['text/csv']}
          filesLimit={1}
          initialFiles={file ? [file] : []}
          useChipsForPreview
          dropzoneText={t('drag-and-drop-a-csv-file-here-or-click')}
          onChange={(files) => handleCsvSelected(files)}
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={handleNext}>{t('next')}</Button>
        </Box>
      </React.Fragment>
    );
  };

  const renderCsvReadyData = () => {
    if (!readyCsvData.length) {
      return (
        <Box component="p" sx={{ p: 2, border: '1px solid grey', backgroundColor: '#f1f1f1' }}>
          There is no valid company entity found.
        </Box>
      );
    }
    return <CsvTable columns={exampleColumns} data={readyCsvData} />;
  };

  const renderInvalidCsvData = () => {
    return <CsvTable columns={invalidTableColumn} data={invalidCsvData} />;
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const renderPreview = () => {
    return (
      <React.Fragment>
        <Typography sx={{ mt: 2, mb: 1 }}>
          {t('Once upload, all the products will be verified by CoriolisESG.')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <TabContext value={tabValue}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChangeTab} aria-label="lab API tabs example">
                  <Tab label={`${t('ready-to-import')} (${readyCsvData.length})`} value="1" />
                  <Tab label={`${t('invalid')} (${invalidCsvData.length})`} value="2" />
                </TabList>
              </Box>
              <TabPanel value="1">{renderCsvReadyData()}</TabPanel>
              <TabPanel value="2">{renderInvalidCsvData()}</TabPanel>
            </TabContext>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
            {t('back')}
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />

          <LoadingButton
            disabled={Boolean(invalidCsvData?.length)}
            loading={uploading}
            loadingPosition="start"
            color="info"
            onClick={handleUpload}
            variant="contained"
            startIcon={<UploadFile />}
          >
            {t('upload')}
          </LoadingButton>
        </Box>
      </React.Fragment>
    );
  };

  const renderResult = () => {
    if (uploadError) {
      return (
        <React.Fragment>
          <Alert severity="error">
            <AlertTitle>{t('error')}</AlertTitle>
            {t('there-is-an-error-occured')}: <strong>{uploadError}</strong>
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button onClick={onFinish}>Close</Button>
          </Box>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <Alert severity="success">
          <AlertTitle>{t('uploaded')}</AlertTitle>
          {t('click-the-button-below-to-refresh-the-company-list')}
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={onFinish}>{t('refresh')}</Button>
        </Box>
      </React.Fragment>
    );
  };

  if (activeStep === steps.length) {
    return <Box sx={{ width: '100%' }}>{renderResult()}</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === 0 && renderCSVUpload()}
      {activeStep === 1 && renderPreview()}
    </Box>
  );
}
